import PropTypes from 'prop-types';
import React from 'react';
import { locationShape, routerShape } from 'react-router/lib/PropTypes';

import t from '../../lib/I18n';
import { PointStages, SeasonStages, BreakPoints } from '../../constants/AppConstants';
import SeasonFlow from '../../flows/SeasonFlow';
import SeasonSearchActions from '../../actions/SeasonSearchActions';
import JourneyNotificationActions from '../../actions/JourneyNotificationActions';
import PreviousSearchesStore from '../../stores/PreviousSearchesStore';
import Railcard1617Store from '../../stores/Railcard1617Store';
import User from '../../records/User';
import SeasonSearch from '../../records/SeasonSearch';
import connectToStores from '../../helpers/connectToStores';
import TrackingActions from "../../actions/TrackingActions";
import FeatureFlagStore from "../../stores/FeatureFlagStore";
import OrderStore from '../../stores/OrderStore';
import urls from '../../urls';

import SearchLeftColumn from '../search/LeftColumn';
import SeasonDetailsForm from '../search/SeasonDetailsForm';
import ResponsiveRender from '../ResponsiveRender';
import StationFinder from '../stationFinder';
import SearchFooter from '../search/Footer';
import JourneyNotificationsModal from '../search/JourneyNotificationsModal';
import AccountRailcard from '../../records/AccountRailcard';
import StationFinderOpenStreetMaps from '../stationFinder/OpenStreetMaps';
import config from '../../config';
import Order from '../../records/Order';


function _getValidationErrors (search) {
    return search.getValidationErrors();
}

export class SeasonSearchForm extends React.PureComponent {

    static propTypes = {
        user: PropTypes.instanceOf(User),
        search: PropTypes.instanceOf(SeasonSearch).isRequired,
        railcard1617: PropTypes.instanceOf(AccountRailcard),
        railcard1617Checked: PropTypes.bool,
        location: locationShape,
        hasFooterPoweredByOn: PropTypes.bool,
        currentOrder: PropTypes.instanceOf(Order),
        router: routerShape,
    };

    static defaultProps = {
        railcard1617: null,
        railcard1617Checked: null,
        location: null
    };

    static contextTypes = {
        router: routerShape
    };

    state = {
        loading: false,
        search: this.props.search,
        searchErrors: _getValidationErrors(this.props.search),
        showJourneyNotificationsModal: false,
        searchAttempted: false,
    };

    componentDidMount() {
        if (!FeatureFlagStore.get('enableMap')) {
            this.props.router.push(urls.location(PointStages.SEARCH));
        }

        const { railcard1617 } = this.props;

        if (this.props.railcard1617Checked !== false) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({ search: this.props.search.set('railcard1617', railcard1617) });
        }
    }

    UNSAFE_componentWillReceiveProps (nextProps) {
        if (this.props.railcard1617) {
            const updatedSearch = this.state.search.set('railcard1617', nextProps.railcard1617Checked ? this.props.railcard1617 : null);

            this.setState({ search: updatedSearch });
        }
    }

    _handleSearchChange = (search) => {
        const searchErrors = _getValidationErrors(search);

        // Make sure the annual is unchecked if it's not available
        search = search.set('annual', search.annual && search.isAnnualAvailable());

        this.setState({ search, searchErrors });
    };

    _handleStationChange = (stations) => {
        const search = this.state.search
            .set('origin', stations.origin)
            .set('destination', stations.destination);

        this._handleSearchChange(search);
    };

    _getStations () {
        const search = this.state.search;

        return {
            'origin': search.origin,
            'destination': search.destination
        };
    }

    _handleStationFinderChange = (type, value) => {
        const stations = { ...this._getStations(), [type]: value };

        this._handleStationChange(stations);
    }

    _handleContinueSearch = async () => {
        this._handleJourneyNotificationsModalClose();
        JourneyNotificationActions.dismissMessages();

        let { search } = this.state;
        const { user, currentOrder, railcard1617Checked } = this.props;

        try {
            this.setState({ loading: true });

            const hasTicketWithPromo = currentOrder && currentOrder.hasPromoCode();
            search = search.set('orderId', hasTicketWithPromo ? currentOrder.id : null);
            search = search.set('isRailcard1617Checked', Boolean(railcard1617Checked));

            await SeasonSearchActions.search({ search, user });
            SeasonFlow.moveToNext(SeasonStages.SEARCH, { search }, this.context.router);
        } catch (e) {
            this.setState({ loading: false });
            throw e;
        }
    };

    _handleJourneyNotificationsModalClose = () => {
        if (this.state.showJourneyNotificationsModal) {
            this.setState({ showJourneyNotificationsModal: false });
        }
    };

    _handleSearch = async () => {
        const { search } = this.state;

        this.setState({ searchAttempted: true });

        TrackingActions.widgetSearchClick({ name: 'search' });

        const searchErrors = _getValidationErrors(search);
        if (!searchErrors.isEmpty()) {
            this.setState({ searchErrors });
            return;
        }

        try {
            this.setState({ loading: true });
            const { messages } = await JourneyNotificationActions.fetchMessages(search);
            if (messages.size > 0) {
                this.setState({ loading: false, showJourneyNotificationsModal: true });
            } else {
                this._handleContinueSearch();
            }
        } catch (e) {
            this.setState({ loading: false });
            throw e;
        }
    };

    render () {
        const { user, hasFooterPoweredByOn } = this.props;
        const { search, searchAttempted, searchErrors, loading, showJourneyNotificationsModal } = this.state;
        const shouldUseOpenStreetMaps = config.get('enableOpenMaps');

        return (
            <div>
                <SearchLeftColumn loading={ loading } title={ t('pageTitles.season-search') }>
                    <SeasonDetailsForm
                        enableStationFinder
                        search={ search }
                        searchAttempted={ searchAttempted }
                        user={ user }
                        handleSearchChange={ this._handleSearchChange }/>
                </SearchLeftColumn>
                <JourneyNotificationsModal
                    show={ showJourneyNotificationsModal }
                    onClose={ this._handleJourneyNotificationsModalClose }
                    onContinue={ this._handleContinueSearch }
                />
                <SearchFooter
                    disabled={ loading || (searchAttempted && !searchErrors.isEmpty()) }
                    onSearch={ this._handleSearch }
                    searchAttempted={ searchAttempted }
                    type={ 'season' }
                    hasFooterPoweredByOn={ hasFooterPoweredByOn }
                />

                <ResponsiveRender minWidth={ BreakPoints.TABLET_PORTRAIT_WIDTH }>
                    {shouldUseOpenStreetMaps ? <StationFinderOpenStreetMaps
                        className="right-col"
                        isMobile={ false }
                        currentSearch={ search }
                        onChange={ this._handleStationFinderChange }
                        origin={ search.origin }
                        destination={ search.destination } /> : <StationFinder
                        className="right-col"
                        isMobile={ false }
                        currentSearch={ search }
                        onChange={ this._handleStationFinderChange }
                        origin={ search.origin }
                        destination={ search.destination } />}
                </ResponsiveRender>
            </div>
        );
    }
}

function getStateFromStores () {
    let search = PreviousSearchesStore.get('season');
    const hasFooterPoweredByOn = FeatureFlagStore.get('hasFooterPoweredByOn');

    return {
        railcard1617Checked: Railcard1617Store.get('checked'),
        railcard1617: Railcard1617Store.getRailcard(),
        search,
        hasFooterPoweredByOn,
        currentOrder: OrderStore.getCurrent()
    };
}

export default connectToStores([PreviousSearchesStore, Railcard1617Store, FeatureFlagStore, OrderStore], getStateFromStores)(SeasonSearchForm);



// WEBPACK FOOTER //
// ./src/components/seasonTicket/SeasonSearchForm.js